import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '@clerk/clerk-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Car,
  ExternalLink,
  Gauge,
  Loader2,
  Paperclip,
  Pencil,
  Plus,
  Receipt,
  Trash2,
  User,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { TFunction } from 'i18next'

import {
  LiveAppointmentTile,
  type LiveAppointmentTileEntry,
} from '@/components/appointments/LiveAppointmentTile'
import {
  CreatedByTooltip,
  type CreatedByInfo,
} from '@/components/CreatedByTooltip'
import { CustomerDialog } from '@/components/customers/CustomerDialog'
import { DeleteRecordDialog } from '@/components/DeleteRecordDialog'
import { EditableTags } from '@/components/EditableTags'
import { VinChip } from '@/components/VinChip'
import { VehicleDialog } from '@/components/vehicles/VehicleDialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { useCanDeleteRecords } from '@/lib/auth'
import { deleteBlockerMessage, type DeleteErrorBody } from '@/lib/deleteRecord'
import { formatDate, formatRelative } from '@/lib/dates'
import { formatMoney } from '@/lib/format'
import { PhoneLink } from '@/components/PhoneLink'
import type { Tag } from '@/lib/tags'
import { useAssistantName } from '@/lib/use-assistant-name'

type Vehicle = {
  id: string
  year: number | null
  make: string | null
  model: string | null
  trim: string | null
  engine: string | null
  drivetrain: string | null
  body: string | null
  vin: string | null
  plate: string | null
  mileage: number | null
  color: string | null
}

type ServiceHistoryRow = {
  id: string
  vehicle_id: string
  service_date: string
  service: string
  total_cad: number
  vehicle_year: number | null
  vehicle_model: string | null
}

type CallRow = {
  id: string
  status: string
  dealer_name: string | null
  part_description: string | null
  price: number
  when_relative: string | null
  when_full: string | null
  started_at: string | null
}

type EstimateRow = {
  id: string
  work_description: string | null
  total: number
  created_at: string
}

type AppointmentSurfaceRow = {
  id: string
  starts_at: string
  ends_at: string
  status: string
  notes: string | null
  vehicle_id: string | null
  vehicle_year: number | null
  vehicle_make: string | null
  vehicle_model: string | null
  vehicle_plate: string | null
}

type CompletedAppointmentRow = {
  id: string
  starts_at: string
  ends_at: string
  updated_at: string | null
  notes: string | null
  vehicle_id: string | null
  vehicle_year: number | null
  vehicle_make: string | null
  vehicle_model: string | null
}

type MessageRow = {
  id: string
  direction: string
  body: string
  sent_at: string | null
  created_at: string
}

type AttachmentRow = {
  id: string
  kind: string
  r2_key: string
  content_type: string | null
  total_cents: number | null
  currency: string | null
  summary: string | null
  captured_at: string
  vehicle_id: string | null
  appointment_id: string | null
}

type AttachmentsResponse = {
  attachments: AttachmentRow[]
}

type Customer = {
  id: string
  name: string
  phone: string | null
  email: string | null
  address: string | null
  casl_consent: string | null
  casl_consent_date: string | null
  first_visit: string | null
  last_visit: string | null
  lifetime_value_cad: number
  notes: string | null
  tags: string[] | null
  preferred_contact: string | null
  created_at: string | null
  created_by: CreatedByInfo
}

type CustomerDetailResponse = {
  customer: Customer
  vehicles: Vehicle[]
  service_history: ServiceHistoryRow[]
  calls: CallRow[]
  estimates: EstimateRow[]
  live_appointments?: AppointmentSurfaceRow[]
  upcoming_appointments?: AppointmentSurfaceRow[]
  completed_appointments?: CompletedAppointmentRow[]
  messages?: MessageRow[]
  // Relational chips added by the tags-v1 deploy. `tag_chips` is the
  // new source of truth; `customer.tags` (text[]) is retained for one
  // PR cycle for rollback safety and removed in a follow-up.
  tag_chips?: Tag[]
}

function toTileEntry(row: AppointmentSurfaceRow): LiveAppointmentTileEntry {
  const vehicleLabel =
    [row.vehicle_year, row.vehicle_make, row.vehicle_model]
      .filter(Boolean)
      .join(' ') || null
  return {
    id: row.id,
    starts_at: row.starts_at,
    ends_at: row.ends_at,
    status: row.status,
    notes: row.notes,
    vehicle_label: vehicleLabel,
  }
}

type ActivityKind = 'service' | 'call' | 'estimate' | 'appointment' | 'sms'

type ActivityEvent = {
  key: string
  kind: ActivityKind
  when: string
  label: string
  amount: string
  sortKey: string
}

// CASL = Canadian Anti-Spam Legislation. Three consent shapes are tracked
// per the data model: explicit signup, implied via prior paid service, and
// implied via inquiry. The DB enum keys (`express`, `implied_existing_customer`,
// `implied_inquiry`) stay in English internal-shape; the display labels and
// explanations live in `customers:detail.casl.{label,explanation}.<key>`.
function caslLabel(
  t: TFunction<'customers'>,
  consent: string | null,
): string {
  if (!consent) return '—'
  // i18next returns the key string verbatim when missing; supply
  // `defaultValue` so unknown enum values render as the raw value
  // rather than the dot-notation key.
  return t(`detail.casl.label.${consent}`, { defaultValue: consent })
}

function caslExplanation(
  t: TFunction<'customers'>,
  consent: string | null,
): string {
  if (!consent) return ''
  return t(`detail.casl.explanation.${consent}`, { defaultValue: '' })
}

function buildActivity(detail: CustomerDetailResponse): ActivityEvent[] {
  const events: ActivityEvent[] = []

  for (const sh of detail.service_history) {
    events.push({
      key: `svc-${sh.id}`,
      kind: 'service',
      when: formatDate(sh.service_date),
      label: `${sh.vehicle_year ?? ''} ${sh.vehicle_model ?? ''} — ${sh.service}`.trim(),
      amount: formatMoney(sh.total_cad),
      sortKey: sh.service_date,
    })
  }

  for (const call of detail.calls) {
    // Prefer a relative timestamp from `started_at` when present; fall
    // back to the seed-baked `when_relative` / `when_full` strings for
    // calls that haven't recorded a real start time yet.
    const when = call.started_at
      ? formatRelative(call.started_at)
      : call.when_relative ?? call.when_full ?? ''
    const label = `${call.dealer_name ?? '—'} — ${call.part_description ?? ''}`
    events.push({
      key: `call-${call.id}`,
      kind: 'call',
      when,
      label,
      amount: call.price > 0 ? formatMoney(call.price) : '',
      sortKey: call.started_at ?? call.when_full ?? '',
    })
  }

  for (const est of detail.estimates) {
    events.push({
      key: `est-${est.id}`,
      kind: 'estimate',
      when: formatRelative(est.created_at),
      label: est.work_description ?? '',
      amount: formatMoney(est.total),
      sortKey: est.created_at,
    })
  }

  for (const appt of detail.completed_appointments ?? []) {
    // Prefer updatedAt (the moment status flipped to 'completed') over
    // endsAt (the scheduled end) — for a "what happened" timeline the
    // actual completion moment is more meaningful than the slot edge.
    const at = appt.updated_at ?? appt.ends_at
    const vehicleLabel =
      [appt.vehicle_year, appt.vehicle_make, appt.vehicle_model]
        .filter(Boolean)
        .join(' ') || ''
    const label = [vehicleLabel, appt.notes].filter(Boolean).join(' — ')
    events.push({
      key: `appt-${appt.id}`,
      kind: 'appointment',
      when: formatRelative(at),
      label: label || vehicleLabel || '',
      amount: '',
      sortKey: at,
    })
  }

  for (const msg of detail.messages ?? []) {
    const at = msg.sent_at ?? msg.created_at
    // Arrow communicates direction at a glance — inbound = from customer
    // (→ us), outbound = from shop (← us). Body truncated to keep the
    // row scannable; full thread lives at /messaging.
    const arrow = msg.direction === 'inbound' ? '→' : '←'
    const body = msg.body.length > 80 ? `${msg.body.slice(0, 79)}…` : msg.body
    events.push({
      key: `msg-${msg.id}`,
      kind: 'sms',
      when: formatRelative(at),
      label: `${arrow} ${body}`,
      amount: '',
      sortKey: at,
    })
  }

  // Recent first. localeCompare treats empty strings as "smallest" which
  // pushes events without a sortable timestamp to the bottom — acceptable
  // for now; once Phase 3 makes started_at non-null on calls this is moot.
  events.sort((a, b) => b.sortKey.localeCompare(a.sortKey))
  return events.slice(0, 20)
}

/**
 * Inline-editable notes card. Read mode shows the saved text (or an
 * "empty" hint) with a small pencil; edit mode swaps in a Textarea +
 * Save / Cancel. Save fires PATCH /api/customers/:id with just the
 * notes field, then writes the new value into the cached query data so
 * the surrounding detail view doesn't refetch the entire customer.
 */
function EditableNotes({
  customerId,
  initialNotes,
}: {
  customerId: string
  initialNotes: string | null
}) {
  const { t } = useTranslation('customers')
  const { t: tCommon } = useTranslation('common')
  const { getToken } = useAuth()
  const qc = useQueryClient()

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(initialNotes ?? '')
  const [error, setError] = useState<string | null>(null)

  // Sync the in-memory copy with the row when navigating between
  // customers without unmounting — without this, opening Customer B
  // after editing A's draft would still show A's text. Pattern A
  // (You Might Not Need an Effect): snapshot the previous initialNotes
  // and in-render setState when it shifts while not editing.
  const [prevInitialNotes, setPrevInitialNotes] = useState(initialNotes)
  if (initialNotes !== prevInitialNotes) {
    setPrevInitialNotes(initialNotes)
    if (!editing) setDraft(initialNotes ?? '')
  }

  const mutation = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const trimmed = draft.trim()
      const res = await fetch(`/api/customers/${customerId}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ notes: trimmed.length > 0 ? trimmed : null }),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(text || t('detail.notes_error_save'))
      }
      return trimmed.length > 0 ? trimmed : null
    },
    onSuccess: (saved) => {
      // Patch the cached customer detail so the surrounding card reads
      // the new value immediately without a full refetch.
      qc.setQueryData(['customer', customerId], (prev: unknown) => {
        if (!prev || typeof prev !== 'object' || prev === null) return prev
        const detail = prev as { customer?: { notes?: string | null } }
        if (!detail.customer) return prev
        return { ...detail, customer: { ...detail.customer, notes: saved } }
      })
      // Invalidate the customer list so any preview / search there
      // catches up too.
      qc.invalidateQueries({ queryKey: ['customers'] })
      setEditing(false)
      setError(null)
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : String(err))
    },
  })

  const display = initialNotes ?? ''

  return (
    <div className="rounded-[14px] border border-border bg-background p-3.5">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="text-[13px] font-semibold text-muted-foreground">
          {t('detail.notes_header')}
        </div>
        {!editing ? (
          <Button
            type="button"
            variant="ghost"
            size="pill"
            className="-my-3 text-[13px] text-muted-foreground"
            onClick={() => {
              setDraft(display)
              setError(null)
              setEditing(true)
            }}
            aria-label={t('detail.notes_edit')}
          >
            <Pencil className="size-3.5" />
            {t('detail.notes_edit')}
          </Button>
        ) : null}
      </div>

      {editing ? (
        <div className="space-y-2">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t('detail.notes_placeholder')}
            rows={5}
            maxLength={2000}
            autoFocus
            disabled={mutation.isPending}
          />
          {error ? (
            <div className="rounded-[10px] border border-destructive/25 bg-destructive-soft p-2 text-xs text-destructive">
              {error}
            </div>
          ) : null}
          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="pill"
              onClick={() => {
                setDraft(display)
                setEditing(false)
                setError(null)
              }}
              disabled={mutation.isPending}
            >
              {t('detail.notes_cancel')}
            </Button>
            <Button
              type="button"
              size="pill"
              onClick={() => {
                setError(null)
                mutation.mutate()
              }}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  {t('detail.notes_saving')}
                </>
              ) : (
                t('detail.notes_save')
              )}
            </Button>
          </div>
        </div>
      ) : (
        <div
          className={`rounded-[10px] bg-muted p-3 text-sm ${
            display ? 'text-foreground whitespace-pre-wrap' : 'italic text-muted-foreground'
          }`}
        >
          {display || t('detail.notes_empty')}
        </div>
      )}
    </div>
  )
}

// Receipts and other saved photos (receipts, VIN plates, odometer shots)
// captured against this customer. Icon keyed by `kind`; unknown kinds fall
// back to a neutral paperclip so a future attachment type still renders.
const ATTACHMENT_ICONS: Record<string, LucideIcon> = {
  receipt: Receipt,
  vin_photo: Car,
  odometer_photo: Gauge,
  other: Paperclip,
}

/**
 * "Receipts & attachments" section. Lists every saved attachment for the
 * customer (newest first, server-ordered) and gives each row an Open
 * affordance.
 *
 * Open can't be a plain `<a href>` / `<img src>` — the image route is
 * Bearer-authed and a browser navigation/img request can't carry the
 * header. So we fetch the bytes as an authenticated blob, mint an object
 * URL, open it in a new tab, then revoke the URL on a short delay (the
 * new tab needs the URL to still resolve when it loads).
 *
 * Renders nothing when the customer has no attachments — no empty state,
 * the section simply disappears (handled by the caller via `count`).
 */
function CustomerAttachments({ customerId }: { customerId: string }) {
  const { t } = useTranslation('customers')
  const { t: tCommon } = useTranslation('common')
  const { getToken } = useAuth()

  // Which row is mid-fetch (so only that Open button shows a spinner).
  const [openingId, setOpeningId] = useState<string | null>(null)
  const [openError, setOpenError] = useState<string | null>(null)

  const attachmentsQuery = useQuery({
    queryKey: ['customer', customerId, 'attachments'],
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))

      const res = await fetch(
        `/api/dashboard/customer/${customerId}/attachments`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as AttachmentsResponse
    },
  })

  async function openAttachment(row: AttachmentRow) {
    if (openingId) return
    setOpeningId(row.id)
    setOpenError(null)
    try {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))

      const res = await fetch(`/api/dashboard/attachment/${row.id}/image`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error(t('detail.attachments.open_error'))

      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank', 'noopener,noreferrer')
      // The opened tab reads the URL asynchronously after it loads; revoke
      // on a delay rather than immediately so it doesn't 404 the new tab.
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (err) {
      setOpenError(
        err instanceof Error ? err.message : t('detail.attachments.open_error'),
      )
    } finally {
      setOpeningId(null)
    }
  }

  const attachments = attachmentsQuery.data?.attachments ?? []

  // Hide the whole section when empty (no empty-state by spec).
  if (attachments.length === 0) return null

  return (
    <div>
      <h2 className="mb-2 text-[13px] font-semibold text-muted-foreground">
        {t('detail.attachments.header')}
      </h2>
      {openError ? (
        <div className="mb-3 rounded-[10px] border border-destructive/25 bg-destructive-soft p-2 text-xs text-destructive">
          {openError}
        </div>
      ) : null}
      <div className="overflow-hidden rounded-[14px] border border-border bg-background">
        {attachments.map((row) => {
          const Icon = ATTACHMENT_ICONS[row.kind] ?? Paperclip
          const kindLabel = t(`detail.attachments.kind.${row.kind}`, {
            defaultValue: t('detail.attachments.kind.other'),
          })
          const title = row.summary?.trim() || kindLabel
          const total =
            row.total_cents !== null
              ? formatMoney(row.total_cents / 100, {
                  currency: row.currency ?? undefined,
                })
              : ''
          const isOpening = openingId === row.id

          return (
            <div
              key={row.id}
              className="flex min-h-[60px] items-center gap-3 border-t border-border px-3.5 py-3 text-sm first:border-t-0"
            >
              <span className="flex size-[38px] shrink-0 items-center justify-center rounded-[10px] bg-muted text-muted-foreground">
                <Icon className="size-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[15px] font-[620] text-foreground">
                  {title}
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[12px] text-ink-3">
                  <span className="font-mono tabular-nums">
                    {formatDate(row.captured_at)}
                  </span>
                  {total ? (
                    <>
                      <span aria-hidden="true">·</span>
                      <span className="font-mono tabular-nums">{total}</span>
                    </>
                  ) : null}
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="pill"
                className="shrink-0 transition-transform active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100"
                onClick={() => openAttachment(row)}
                disabled={isOpening}
                aria-label={t('detail.attachments.open_aria', { label: title })}
              >
                {isOpening ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    {t('detail.attachments.opening')}
                  </>
                ) : (
                  <>
                    <ExternalLink className="size-4" />
                    {t('detail.attachments.open')}
                  </>
                )}
              </Button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export function CustomerDetail() {
  const { t } = useTranslation('customers')
  const { t: tCommon } = useTranslation('common')
  const { id: customerId } = useParams<{ id: string }>()
  const { getToken, isLoaded } = useAuth()
  const [editOpen, setEditOpen] = useState(false)
  const [addVehicleOpen, setAddVehicleOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const navigate = useNavigate()
  const qc = useQueryClient()
  const canDelete = useCanDeleteRecords()
  // §2.1 assistant lane badge — tenant assistant initial, never hardcoded.
  const assistantName = useAssistantName()

  const detailQuery = useQuery({
    queryKey: ['customer', customerId],
    enabled: isLoaded && !!customerId,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))

      const res = await fetch(`/api/customers/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (res.status === 404) return null
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }

      return (await res.json()) as CustomerDetailResponse
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))

      const res = await fetch(`/api/customers/${customerId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })

      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as DeleteErrorBody
        const blocked = deleteBlockerMessage(t, res.status, body)
        if (blocked) throw new Error(blocked)
        throw new Error(tCommon('error.request_failed', { status: res.status }))
      }

      return (await res.json()) as { ok: true; orphaned_vehicle_count: number }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers'] })
      qc.invalidateQueries({ queryKey: ['vehicles'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      navigate('/customers')
    },
    onError: (err) => {
      setDeleteError(
        err instanceof Error ? err.message : t('detail.delete.error'),
      )
    },
  })

  const activity = useMemo(
    () => (detailQuery.data ? buildActivity(detailQuery.data) : []),
    [detailQuery.data],
  )

  if (detailQuery.isLoading) {
    return (
      <div className="mx-auto w-full max-w-[660px] rounded-[14px] border border-border bg-background px-3.5 py-12 text-center text-[13px] text-muted-foreground lg:max-w-[720px]">
        {tCommon('loading')}
      </div>
    )
  }

  if (detailQuery.isError) {
    return (
      <div className="mx-auto w-full max-w-[660px] rounded-[14px] border border-destructive/25 bg-destructive-soft p-4 text-sm text-destructive lg:max-w-[720px]">
        {detailQuery.error instanceof Error
          ? detailQuery.error.message
          : t('detail.error')}
      </div>
    )
  }

  if (!detailQuery.data) {
    return (
      <div className="mx-auto w-full max-w-[660px] space-y-4 lg:max-w-[720px]">
        <Link
          to="/customers"
          className="inline-flex min-h-11 items-center gap-1 rounded-[10px] pl-0.5 pr-2.5 text-[15px] font-medium text-muted-foreground transition-[color,transform] duration-[120ms] active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100 [@media(hover:hover)_and_(pointer:fine)]:hover:text-foreground"
        >
          {t('detail.back')}
        </Link>
        <div className="rounded-[14px] border border-border bg-background px-3.5 py-12 text-center text-[13px] text-muted-foreground">
          {t('detail.not_found')}
        </div>
      </div>
    )
  }

  const { customer, vehicles } = detailQuery.data
  const tagChips = detailQuery.data.tag_chips ?? []

  return (
    <div className="mx-auto w-full max-w-[660px] space-y-6 lg:max-w-[720px]">
      {/* §4.5 detail header: 44px back-button row in place of the shop line */}
      <Link
        to="/customers"
        className="inline-flex min-h-11 items-center gap-1 rounded-[10px] pl-0.5 pr-2.5 text-[15px] font-medium text-muted-foreground transition-[color,transform] duration-[120ms] active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100 [@media(hover:hover)_and_(pointer:fine)]:hover:text-foreground"
      >
        {t('detail.back')}
      </Link>

      {/* Header card */}
      <div className="flex flex-wrap items-start gap-4 rounded-[14px] border border-border bg-background p-3.5">
        <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-surface-2 text-xl font-bold text-muted-foreground">
          {customer.name.charAt(0).toUpperCase()}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-[22px] font-bold tracking-[-0.015em] text-foreground [text-wrap:balance]">
              {customer.name}
            </h1>
            <CreatedByTooltip
              createdAt={customer.created_at}
              createdBy={customer.created_by}
            />
            <EditableTags
              entityType="customer"
              entityId={customer.id}
              tags={tagChips}
            />
          </div>
          <div className="mt-1 grid grid-cols-1 gap-x-6 gap-y-1 text-[13px] text-muted-foreground sm:grid-cols-2">
            <div>
              <span className="mr-2 text-[12px] font-semibold text-ink-3">
                {t('detail.labels.phone')}
              </span>
              <PhoneLink phone={customer.phone} className="font-mono tabular-nums" />
            </div>
            <div>
              <span className="mr-2 text-[12px] font-semibold text-ink-3">
                {t('detail.labels.email')}
              </span>
              <span>{customer.email ?? '—'}</span>
            </div>
            <div>
              <span className="mr-2 text-[12px] font-semibold text-ink-3">
                {t('detail.labels.address')}
              </span>
              <span>{customer.address ?? '—'}</span>
            </div>
            <div>
              <span className="mr-2 text-[12px] font-semibold text-ink-3">
                {t('detail.labels.prefers')}
              </span>
              <span>{customer.preferred_contact ?? '—'}</span>
            </div>
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <Button
            type="button"
            size="pill"
            variant="outline"
            onClick={() => setEditOpen(true)}
          >
            <Pencil className="size-3.5" />
            {t('detail.edit')}
          </Button>
          {canDelete ? (
            <Button
              type="button"
              size="pill"
              variant="outline"
              className="text-destructive [@media(hover:hover)_and_(pointer:fine)]:hover:bg-destructive-soft [@media(hover:hover)_and_(pointer:fine)]:hover:text-destructive"
              onClick={() => {
                setDeleteError(null)
                setDeleteOpen(true)
              }}
            >
              <Trash2 className="size-3.5" />
              {tCommon('button.delete')}
            </Button>
          ) : null}
          <div className="text-right">
            <div className="text-[12px] font-medium text-ink-3">
              {t('detail.lifetime_value')}
            </div>
            <div className="font-mono text-[22px] font-bold tabular-nums text-foreground">
              {formatMoney(customer.lifetime_value_cad)}
            </div>
            <div className="mt-0.5 text-[12px] text-ink-3">
              {customer.first_visit
                ? t('detail.since', { date: formatDate(customer.first_visit) })
                : t('detail.new_inquiry')}
            </div>
          </div>
        </div>
      </div>

      <CustomerDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        seed={{
          id: customer.id,
          name: customer.name,
          phone: customer.phone,
          email: customer.email,
          address: customer.address,
          notes: customer.notes,
          preferred_contact: customer.preferred_contact,
        }}
      />

      <DeleteRecordDialog
        open={deleteOpen}
        onOpenChange={(next) => {
          setDeleteOpen(next)
          if (!next) setDeleteError(null)
        }}
        title={t('detail.delete.title')}
        description={
          <>
            {t('detail.delete.description', { name: customer.name })}
            {vehicles.length > 0 ? (
              <span className="mt-1 block">
                {t('detail.delete.orphan_note', { count: vehicles.length })}
              </span>
            ) : null}
          </>
        }
        confirmLabel={t('detail.delete.confirm')}
        pending={deleteMutation.isPending}
        errorMessage={deleteError}
        onConfirm={() => {
          setDeleteError(null)
          deleteMutation.mutate()
        }}
      />

      {/* Live + upcoming appointment surface — only renders when there's
          something current to show. Live takes priority; upcoming is a
          softer chip so multiple don't pile up visually. */}
      {(detailQuery.data.live_appointments?.length ?? 0) > 0 ||
      (detailQuery.data.upcoming_appointments?.length ?? 0) > 0 ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {detailQuery.data.live_appointments?.map((row) => (
            <LiveAppointmentTile
              key={row.id}
              appointment={toTileEntry(row)}
              kind="live"
            />
          ))}
          {detailQuery.data.upcoming_appointments?.map((row) => (
            <LiveAppointmentTile
              key={row.id}
              appointment={toTileEntry(row)}
              kind="upcoming"
            />
          ))}
        </div>
      ) : null}

      {/* Notes + CASL */}
      <div className="grid gap-4 md:grid-cols-2">
        <EditableNotes
          customerId={customer.id}
          initialNotes={customer.notes}
        />
        <div className="rounded-[14px] border border-border bg-background p-3.5">
          <div className="mb-2 text-[13px] font-semibold text-muted-foreground">
            {t('detail.casl.header')}
          </div>
          <div className="text-sm">
            <span className="font-medium text-foreground">
              {caslLabel(t, customer.casl_consent)}
            </span>
            {customer.casl_consent_date ? (
              <span className="ml-2 text-[12px] text-ink-3">
                {t('detail.casl.since', {
                  date: formatDate(customer.casl_consent_date),
                })}
              </span>
            ) : null}
          </div>
          <div className="mt-1 text-[13px] text-muted-foreground">
            {caslExplanation(t, customer.casl_consent)}
          </div>
        </div>
      </div>

      {/* Vehicles */}
      <div>
        <div className="mb-2 flex items-center justify-between gap-3">
          <h2 className="text-[13px] font-semibold text-muted-foreground">
            {t('detail.vehicles_header')}
          </h2>
          <Button
            type="button"
            size="pill"
            variant="outline"
            className="-my-2"
            onClick={() => setAddVehicleOpen(true)}
          >
            <Plus className="size-4" />
            {t('detail.add_vehicle')}
          </Button>
        </div>

        <VehicleDialog
          open={addVehicleOpen}
          onOpenChange={setAddVehicleOpen}
          seed={{ customer_id: customer.id }}
        />

        {vehicles.length === 0 ? (
          <div className="rounded-[14px] border border-dashed border-border bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground">
            {t('detail.vehicles_empty')}
          </div>
        ) : (
          <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
            {vehicles.map((v) => (
              <Link
                key={v.id}
                to={`/vehicles/${v.id}`}
                className="block rounded-[14px] border border-border bg-background px-3.5 py-3 transition-[background-color,transform] active:scale-[0.99] active:bg-muted [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted"
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="flex size-[38px] shrink-0 items-center justify-center rounded-[10px] bg-muted text-muted-foreground">
                    <Car aria-hidden="true" className="size-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[15px] font-[620] text-foreground">
                      {[v.year, v.make, v.model].filter(Boolean).join(' ') || '—'}
                    </div>
                    <div className="mt-0.5 text-[13px] text-muted-foreground">
                      {[v.engine, v.drivetrain].filter(Boolean).join(' · ') || '—'}
                    </div>
                    {v.vin ? (
                      <div className="mt-1">
                        <VinChip vin={v.vin} size="xs" />
                      </div>
                    ) : null}
                  </div>
                  <div className="shrink-0 text-right text-[12px] text-muted-foreground">
                    <div className="font-mono tabular-nums">
                      {v.mileage !== null
                        ? tCommon('units.km', { value: v.mileage.toLocaleString() })
                        : '—'}
                    </div>
                    <div>{v.color ?? '—'}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Receipts & attachments — self-fetching; renders nothing when empty */}
      <CustomerAttachments customerId={customer.id} />

      {/* Recent activity — §2.1 lane-attributed timeline. The payload
          carries no per-row actor marker yet, so attribution is two-lane:
          calls are the assistant's parts calls (the only kind the product
          attributes to the assistant today) and everything else renders
          as the manual lane. Quick-action (bolt) rows light up once the
          backend lane marker ships. */}
      <div>
        <h2 className="mb-2 text-[13px] font-semibold text-muted-foreground">
          {t('detail.activity_header')}
        </h2>
        <div className="overflow-hidden rounded-[14px] border border-border bg-background">
          {activity.length === 0 ? (
            <div className="px-3.5 py-12 text-center text-[13px] text-muted-foreground">
              {t('detail.activity_empty')}
            </div>
          ) : (
            activity.map((event) => (
              <div
                key={event.key}
                className="flex items-start gap-[11px] border-t border-border px-3.5 py-3 first:border-t-0"
              >
                {event.kind === 'call' ? (
                  <span
                    aria-hidden="true"
                    className="flex size-6 flex-none items-center justify-center rounded-[8px] bg-saved text-[11px] font-bold text-white"
                  >
                    {assistantName.charAt(0).toUpperCase()}
                  </span>
                ) : (
                  <span
                    aria-hidden="true"
                    className="flex size-6 flex-none items-center justify-center rounded-full bg-surface-2 text-muted-foreground"
                  >
                    <User className="size-3.5" />
                  </span>
                )}
                <div className="min-w-0 flex-1 text-[15px] text-foreground">
                  {event.label}
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[12px] text-ink-3">
                    <span>{t(`detail.activity_kind.${event.kind}`)}</span>
                    {event.when ? (
                      <span className="font-mono tabular-nums">{event.when}</span>
                    ) : null}
                    {event.kind === 'call' ? <span>{assistantName}</span> : null}
                  </div>
                </div>
                {event.amount ? (
                  <div className="flex-none text-right font-mono text-[12px] tabular-nums text-ink-3">
                    {event.amount}
                  </div>
                ) : null}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
